/**
 * Скриншоты платформы PromoKit.
 *
 * Источник — https://promo-kit.ru (наш backend-продукт за Bafsy).
 * Собрано автоматически; описания/комментарии — редакторская выжимка со
 * страниц исходного сайта, отредактировано для секции Platform Showcase.
 *
 * i18n: RU — базовое поле, EN — параллельное `*En`-поле. Компонент
 * `HomePlatformShowcase.vue` выбирает через `pickLocalized`.
 */

export interface PlatformScreenshot {
  readonly id: string;
  /** Public URL относительно домена сайта — `/screenshots/promo-kit/...`. */
  readonly src: string;
  /** Короткий заголовок (5–6 слов, Title Case). */
  readonly title: string;
  readonly titleEn?: string;
  /** Описание фичи, 1 короткое предложение. */
  readonly description: string;
  readonly descriptionEn?: string;
  /** Редакторский комментарий: что это даёт пользователю, 1 предложение. */
  readonly comment: string;
  readonly commentEn?: string;
  /** Тематическая группа для фильтров / тура. */
  readonly group: 'builder' | 'crm' | 'analytics' | 'operations' | 'other';
  /** Ссылка на исходную страницу promo-kit.ru, откуда скриншот извлечён. */
  readonly source: string;
  /**
   * Подрезать верхнюю часть изображения в UI (через CSS `object-position`),
   * чтобы скрыть вспомогательный бейдж/бренд в top-strip скриншота.
   * Используется точечно, сам файл не меняется.
   */
  readonly cropTop?: boolean;
}

export const PLATFORM_SCREENSHOTS: readonly PlatformScreenshot[] = [
  {
    id: 'builder-quick-start',
    src: '/screenshots/promo-kit/01-builder-quick-start.png',
    title: 'Мастер выбора типа промо',
    titleEn: 'Promo-type wizard',
    description:
      'Старт проекта: выбор механики — чековое, кодовое, анкета, квиз или индивидуальная сборка.',
    descriptionEn:
      'Kick off a project by picking a mechanic — receipts, codes, a survey, a quiz or a custom build.',
    comment:
      'Уже на первом шаге платформа подсказывает нужные поля под тип акции и экономит 10–15 минут на ручных настройках.',
    commentEn:
      'The first step already surfaces the fields each mechanic needs and saves 10–15 minutes of manual setup.',
    group: 'builder',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'builder-fast-setup',
    src: '/screenshots/promo-kit/02-builder-fast-setup.png',
    title: 'Пошаговая настройка промо',
    titleEn: 'Step-by-step promo setup',
    description:
      'Чек-лист с блоками «Основные настройки», «Участники», «Призы», «Письма и SMS», «Продукты», «Безопасность».',
    descriptionEn:
      'A checklist with blocks for Core settings, Participants, Prizes, Email and SMS, Products, and Security.',
    comment:
      'Видно сколько шагов и минут займёт каждый блок — удобно распределить работу между маркетологом и аналитиком.',
    commentEn:
      'You can see how many steps and minutes each block takes — easy to split the work between the marketer and the analyst.',
    group: 'builder',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'builder-participants',
    src: '/screenshots/promo-kit/03-builder-participants.png',
    title: 'Конструктор анкеты участника',
    titleEn: 'Participant form builder',
    description:
      'Настройка способа авторизации (email/телефон), подтверждения, обязательных полей и согласий.',
    descriptionEn:
      'Configure sign-in method (email or phone), verification, required fields and consents.',
    comment:
      'Без кода собирается ровно та анкета, которая нужна под ФЗ‑152 и бриф — без доработок со стороны разработчиков.',
    commentEn:
      'Assemble exactly the form the brief and data-privacy law require — no-code, no developer hand-off.',
    group: 'builder',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'builder-prizes',
    src: '/screenshots/promo-kit/04-builder-prizes.png',
    title: 'Список призов и механик выдачи',
    titleEn: 'Prizes and fulfilment mechanics',
    description:
      'Таблица призов с типами (электронный, гарантированный, разыгрываемый), тиражом, категорией и ценой.',
    descriptionEn:
      'A prize table with type (digital, guaranteed, drawn), stock, category and price.',
    comment:
      'Сразу видно остаток фонда и периодичность розыгрыша — призовую экономику проверяем до запуска, а не по инцидентам.',
    commentEn:
      'Stock levels and draw cadence are visible up front — we audit prize economics before launch, not after incidents.',
    group: 'operations',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'builder-emails-sms',
    src: '/screenshots/promo-kit/05-builder-emails-sms.png',
    title: 'Настройка писем и SMS',
    titleEn: 'Email and SMS setup',
    description:
      'Выбор каналов коммуникации, параметры отправителя, проверка домена и статус модерации адресов.',
    descriptionEn:
      'Pick communication channels, set sender parameters, verify the domain and review address moderation status.',
    comment:
      'Подхватывает подтверждение домена и модерацию заранее — на старте промо письма не падают в спам.',
    commentEn:
      'Domain verification and moderation are handled up front — email does not land in spam when the promo kicks off.',
    group: 'crm',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'builder-products',
    src: '/screenshots/promo-kit/06-builder-products.png',
    title: 'Продукты и правила чеков',
    titleEn: 'Products and receipt rules',
    description: 'Справочник SKU, алиасы, лимиты на количество и стоимость товаров в одном чеке.',
    descriptionEn:
      'An SKU catalogue with aliases and per-receipt limits on item count and total value.',
    comment:
      'Валидатор чеков опирается на этот справочник — исключает «чужие» товары и защищает акцию от накруток.',
    commentEn:
      'The receipt validator reads from this catalogue — it filters out off-brand items and shields the promo from abuse.',
    group: 'operations',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'builder-security',
    src: '/screenshots/promo-kit/07-builder-security.png',
    title: 'Уровни безопасности промо',
    titleEn: 'Promo security tiers',
    description:
      'Профиль защиты от призоловов: валидация ИНН, ключи доступа, блок‑лист IP, конфигурация участника.',
    descriptionEn:
      'An anti-fraud profile: tax-ID validation, access keys, IP block list and participant configuration.',
    comment:
      'Одним переключателем меняем жёсткость антифрода и можем заказать аудит безопасности прямо из интерфейса.',
    commentEn:
      'Toggle fraud strictness with one switch and request a security audit right from the UI.',
    group: 'operations',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'receipt-validation',
    src: '/screenshots/promo-kit/08-receipt-validation.png',
    title: 'Очередь модерации чеков',
    titleEn: 'Receipt moderation queue',
    description:
      'Таблица чеков с фильтрами «ждут модерацию», «в очереди», «распознан ФНС», «отказанные» и превью фото.',
    descriptionEn:
      'A receipts table with filters for Awaiting moderation, In queue, Recognised by FNS and Declined — plus photo previews.',
    comment:
      'Чеки сверяются с ФНС в реальном времени — оператор видит 5 952 чека в одной очереди вместо десятка Excel‑файлов.',
    commentEn:
      'Receipts are matched against FNS in real time — the operator sees 5,952 receipts in one queue instead of a dozen Excel files.',
    group: 'operations',
    source: 'https://promo-kit.ru/#konstructor',
    cropTop: true,
  },
  {
    id: 'gamification-puzzle',
    src: '/screenshots/promo-kit/09-gamification-puzzle.png',
    title: 'Игровая механика «Пятнашки»',
    titleEn: 'Sliding-tiles game mechanic',
    description: 'Мобильный лендинг с пятнашками: участник собирает картинку из квадратов.',
    descriptionEn:
      'A mobile landing with a sliding-tiles game — players rebuild the picture from squares.',
    comment:
      'Встроенная игра добавляет интерактив без кастомной разработки — вовлечение растёт на той же кампании в разы.',
    commentEn:
      'A built-in game adds interactivity without custom development — engagement jumps on the same campaign.',
    group: 'other',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'gamification-wheel',
    src: '/screenshots/promo-kit/10-gamification-wheel.png',
    title: 'Колесо фортуны',
    titleEn: 'Wheel of fortune',
    description: 'Мгновенная механика: кручение колеса с секторами призов и скидок.',
    descriptionEn: 'An instant-win mechanic: spin a wheel with prize and discount sectors.',
    comment:
      'Колесо фортуны настраивается через конструктор — ставка на удачу усиливает эмоциональную реакцию аудитории.',
    commentEn:
      "The wheel is configured from the builder — a luck-based hook dials up the audience's emotional response.",
    group: 'other',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'gamification-match3',
    src: '/screenshots/promo-kit/11-gamification-match3.png',
    title: 'Игра «Три в ряд»',
    titleEn: 'Match-3 game',
    description: 'Мобильная игра «три в ряд» с таймером 60 секунд, счётом и целью в 1 000 баллов.',
    descriptionEn: 'A mobile match-3 with a 60-second timer, a score and a 1,000-point target.',
    comment:
      'Сессионная механика удерживает участника в промо дольше и плотно интегрируется с баллами и призами.',
    commentEn:
      'A session-based mechanic that keeps players in the promo longer and ties neatly into points and prizes.',
    group: 'other',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'mailing-campaigns',
    src: '/screenshots/promo-kit/12-mailing-campaigns.png',
    title: 'Триггерные письма и SMS',
    titleEn: 'Triggered email and SMS',
    description:
      'Разделы «Редактор писем», «Редактор SMS» и «Документы для оповещений» для победителей и участников.',
    descriptionEn:
      'Email editor, SMS editor and notification-document sections for winners and participants.',
    comment:
      'Триггерные и сервисные рассылки живут в одном разделе — нет переключений между ESP и платформой промо.',
    commentEn:
      'Triggered and service mailings live in one place — no context switching between the ESP and the promo platform.',
    group: 'crm',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'feedback-inbox',
    src: '/screenshots/promo-kit/13-feedback-inbox.png',
    title: 'Чат с менеджером проекта',
    titleEn: 'Project manager chat',
    description:
      'Рабочие каналы команды: брифы, техтребования и оперативные вопросы в одном потоке.',
    descriptionEn:
      "The team's working channels: briefs, tech requirements and day-to-day questions in one thread.",
    comment:
      'Переписка с проект‑менеджером внутри платформы — без внешних мессенджеров и тикет‑систем.',
    commentEn:
      'Talking to the project manager stays inside the platform — no external messengers or ticketing tools.',
    group: 'crm',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'partner-integrations',
    src: '/screenshots/promo-kit/14-partner-integrations.png',
    title: 'Партнёрские сервисы',
    titleEn: 'Partner services',
    description:
      'Партнёрская экосистема сервисов: веб‑аналитика, электронные призы и кешбэк‑интеграции из коробки.',
    descriptionEn:
      'A partner ecosystem: web analytics, digital prizes and cashback integrations out of the box.',
    comment:
      'Электронные призы, кешбэк и веб‑аналитика подключаются галочкой — не нужны отдельные контракты и коннекторы.',
    commentEn:
      'Turn on digital prizes, cashback and web analytics with a checkbox — no separate contracts or connectors.',
    group: 'operations',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'launch-channels',
    src: '/screenshots/promo-kit/15-launch-channels.png',
    title: 'Каналы запуска промо',
    titleEn: 'Promo launch channels',
    description:
      'Одна кампания одновременно живёт как лендинг, как Telegram‑бот и как корпоративный сайт.',
    descriptionEn:
      'One campaign runs simultaneously as a landing page, a Telegram bot and a corporate site.',
    comment:
      'Мультиканальный запуск из одного проекта — акция доступна там, где пользователь уже находится.',
    commentEn:
      'Multi-channel launch from one project — the promo shows up where the user already is.',
    group: 'operations',
    source: 'https://promo-kit.ru/#konstructor',
  },
  {
    id: 'chatbot-promo',
    src: '/screenshots/promo-kit/16-chatbot-promo.png',
    title: 'Промо‑акция в чат‑боте',
    titleEn: 'Chatbot-powered promo',
    description:
      'Telegram‑бот с Black Friday‑сценарием: приветствие, правила, приём чеков и подсчёт баллов.',
    descriptionEn:
      'A Telegram bot with a Black Friday flow: greeting, rules, receipt intake and point scoring.',
    comment:
      'Чат‑бот работает без отдельной разработки: та же механика, но с CTR и удержанием мессенджера.',
    commentEn:
      'The chatbot ships without custom development — same mechanic, but with messenger-level CTR and retention.',
    group: 'other',
    source: 'https://promo-kit.ru/#services',
  },
] as const;
